import React, { useEffect, useState } from "react";
import {
    IResourceComponentsProps,
    useApiUrl,
    useCreate,
    useCreateMany,
    useDelete,
    useGetIdentity,
    useInvalidate,
    useNotification,
    useParsed,
    useShow,
    useTranslate,
    useUpdate,
} from "@refinedev/core";
import { useSelect } from "@refinedev/antd";
import {
    Button,
    Col,
    Flex,
    Grid,
    Row,
    Skeleton,
    Tabs,
    Typography,
    UploadProps,
    theme,
    message,
} from "antd";
import { flattenData } from "../../shared/utils";
import { GlassCard } from "../../shared/components";
import {
    CodeOutlined,
    MessageOutlined,
    SendOutlined,
} from "@ant-design/icons";
import { TOKEN_KEY } from "../../authProvider";
import { OrderStatus } from "../../models/order";
import { Messages } from "./components/messages";
import { NotFound } from "../../components/errors";
import { useAdmin, useTuner } from "../../hooks/auth";
import { useSearchParams } from "react-router-dom";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import {
    OrderCharacteristics,
    OrderAttachments,
    OrderProducts,
    OrderComments,
    AssignmentModal,
    OrderHistoryTimeline,
    OrderAutomationTasks,
    OrderAssignmentCard,
    OrderShowHeader,
    OrderShowBar,
    OrderFileDetailsModal,
    AnalysisResultsModal,
    AssemblyFilesModal,
    OrderSettingsModal,
    generateIniFile,
} from "./components";
import { ServiceProviderProductMappingModal } from "../service-providers/ServiceProviderProductMappingModal";
import { AutomationTaskStatus, AutomationTaskStep } from "../../models/automation-task";
import { useWindowSize } from "../../hooks/useWindowSize";
import {
    useOrderSettings,
    useOrderAssignment,
    useOrderAutomation,
    useOrderMessages,
} from "./hooks/index";
import { OrderMessage } from "./hooks/useOrderMessages";

const { Title } = Typography;

// Wrapper component with toggle mechanism
export const OrderShow: React.FC<IResourceComponentsProps> = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const useRedesign = searchParams.get("redesign") === "true";
    const _t = useTranslate();

    const toggleDesign = () => {
        const newParams = new URLSearchParams(searchParams);
        if (useRedesign) {
            newParams.delete("redesign");
        } else {
            newParams.set("redesign", "true");
        }
        setSearchParams(newParams);
    };

    return (
        <>
            <OrderShowCurrent />

            <div className="flex justify-center mt-8 mb-4 hidden">
                <Button type="dashed" icon={<SendOutlined />} onClick={toggleDesign}>
                    {useRedesign
                        ? _t("orders.redesign.toggleButtonClassic")
                        : _t("orders.redesign.toggleButton")}
                </Button>
            </div>
        </>
    );
};

// Current implementation
const OrderShowCurrent: React.FC = () => {
    const _t = useTranslate();
    const { token } = theme.useToken();
    const { queryResult, setShowId } = useShow({ resource: "orders" });
    const [searchParams] = useSearchParams();
    const { data, isLoading, error } = queryResult;
    const [formValues, setFormValues] = React.useState([] as any[]);
    const apiUrl = useApiUrl();
    const { mutate } = useCreate();
    const { mutate: mutateMany } = useCreateMany();
    const { open } = useNotification();
    const invalidate = useInvalidate();
    const [record, setRecord] = React.useState(data?.data as any);
    const [attachments, setAttachments] = React.useState<any[]>([]);
    const [resultAttachments, setResultAttachments] = React.useState<any[]>([]);
    const { data: user } = useGetIdentity();
    const { id } = useParsed();
    const [messageUploadedFiles, setMessageUploadedFiles] = useState<any[]>([]);
    const isAdmin = useAdmin();
    const isTuner = useTuner();
    const [messageFileList, setMessageFileList] = useState<any[]>([]);
    const [fileList, setFileList] = useState<any[]>([]);
    const [incomingFileList, setIncomingFileList] = useState<any[]>([]);
    const [incomingCategory, setIncomingCategory] = useState<'bin' | 'misc'>('misc');
    const [activeKey, setActiveKey] = useState(
        searchParams.get("view") === "messages" ? "2" : "1"
    );
    const [serviceComments, setServiceComments] = useState(record?.serviceComments);
    const [isSavingServiceComments, setIsSavingServiceComments] = useState(false);
    const [productOptions, setProductOptions] = useState<any[]>([]);
    const [isPrepaid, setIsPrepaid] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
    const [orderStatus, setOrderStatus] = useState(record?.status || "");
    const { width } = useWindowSize();
    const breakpoint = Grid.useBreakpoint();
    const isMobile = typeof breakpoint.lg === "undefined" ? false : !breakpoint.lg;
    const [activeTab, setActiveTab] = useState("details");
    const hasMarkedMessagesReadRef = React.useRef(false);

    // ── Hooks ──────────────────────────────────────────────────────────────────

    const settings = useOrderSettings(apiUrl);

    const assignment = useOrderAssignment({
        record,
        isAdmin: !!isAdmin,
        isTuner: !!isTuner,
        user,
        apiUrl,
    });

    // ── Helpers ────────────────────────────────────────────────────────────────

    const { mutate: update } = useUpdate();
    const { mutate: deleteResource } = useDelete();

    const orderMessages = useOrderMessages(id);
    const markMessagesRead = () => {
        void orderMessages.markAllRead();
    };

    const automation = useOrderAutomation({
        record,
        attachments,
        orderId: id,
        apiUrl,
        refetchOrder: queryResult.refetch,
        isAdmin: !!isAdmin,
        setRecord,
        setOrderStatus,
        markMessagesRead,
    });

    // ── Effects ────────────────────────────────────────────────────────────────

    useEffect(() => {
        setShowId(id);
    }, [id, setShowId]);

    useEffect(() => {
        if (record?.account) {
            setIsPrepaid(record?.account?.billingMode === "prepaid");
        }
        setServiceComments(record?.serviceComments);
        if (record?.products?.length > 0) {
            setSelectedProducts(record?.products?.map((p: any) => p.id));
        }
    }, [record]);

    useEffect(() => {
        if (data?.data) {
            const rec = data.data;
            setRecord(rec);
            setAttachments(rec.attachments.filter((a: any) => a.direction === "in"));
            setResultAttachments(rec.attachments.filter((a: any) => a.direction === "out"));
            setOrderStatus(rec.status);

            if (isAdmin && rec.automationTasks) {
                const analyzeTask = rec.automationTasks.find(
                    (task: any) =>
                        task.step === AutomationTaskStep.analyze &&
                        task.status === AutomationTaskStatus.completed
                );
                if (
                    analyzeTask?.metadata?.analysisResult?.projects &&
                    Array.isArray(analyzeTask.metadata.analysisResult.projects) &&
                    analyzeTask.metadata.analysisResult.projects.length > 0 &&
                    rec.status !== OrderStatus.completed &&
                    rec.status !== OrderStatus.sav
                ) {
                    automation.setAnalysisResult(analyzeTask.metadata.analysisResult);
                }
            }

        }
    }, [data]);

    useEffect(() => {
        hasMarkedMessagesReadRef.current = false;
    }, [record?.id]);

    useEffect(() => {
        const isMessagesVisible = !isMobile || activeTab === "messages";
        if (!record?.id || hasMarkedMessagesReadRef.current || !isMessagesVisible) {
            return;
        }
        if (!isAdmin && !isTuner) {
            return;
        }
        hasMarkedMessagesReadRef.current = true;
        void orderMessages.markAllRead();
    }, [activeTab, isAdmin, isMobile, isTuner, orderMessages, record?.id]);

    useEffect(() => {
        if (record?.payload?.trim()) {
            const parsed = JSON.parse(record.payload);
            const vals = flattenData(parsed);
            setFormValues(vals);

            if (record.status === OrderStatus.submitted && settings.autoStartOrder) {
                update(
                    { resource: "orders", values: { status: OrderStatus.ongoing }, id: record.id },
                    {
                        onSuccess: () => {
                            setOrderStatus(OrderStatus.ongoing);
                            setRecord((prev: any) => ({ ...prev, status: OrderStatus.ongoing }));
                        },
                    }
                );
            }
        }
    }, [record]);

    // ── Product selects ────────────────────────────────────────────────────────

    const { queryResult: productQueryResult } = useSelect({
        resource: "products",
        optionValue: "id",
        searchField: "name",
        sorters: [{ field: "name", order: "asc" }],
    });

    useEffect(() => {
        if (productQueryResult.data && productOptions.length === 0) {
            const groupedOptions: { [key: string]: { label: string; options: any[] } } =
                productQueryResult.data.data.reduce(
                    (groups: { [key: string]: { label: string; options: any[] } }, item: any) => {
                        let group = `${item.group}`;
                        if (group === "null") group = "";
                        if (!groups[group]) groups[group] = { label: group, options: [] };
                        groups[group].options.push({
                            label: isPrepaid
                                ? `${item.name} - ${item.price} ${_t("credits.credits")}`
                                : `${item.name} | ${item.type}`,
                            value: item.id,
                        });
                        return groups;
                    },
                    {}
                );

            const sorted = Object.entries(groupedOptions)
                .sort(([a], [b]) => a.localeCompare(b))
                .reduce(
                    (obj: { [key: string]: { label: string; options: any[] } }, [k, v]) => {
                        obj[k] = v;
                        return obj;
                    },
                    {}
                );
            setProductOptions(Object.values(sorted));
        }
    }, [productQueryResult, isPrepaid]);

    // ── Action handlers ────────────────────────────────────────────────────────

    const field = (name: string): any => formValues.find((f: any) => f.name === name);

    const downloadIniFile = () => {
        const data = formValues.reduce((obj: Record<string, string>, f: any) => {
            obj[f.name] = f.value;
            return obj;
        }, {});
        const out = generateIniFile({ ...data, account: record.account.name });
        saveAs(new Blob([out], { type: "text/plain;charset=utf-8;" }), "winols.ini");
    };

    const downloadIniFileAndClientFiles = async () => {
        const hideLoading = message.loading(_t("orders.actions.downloadZipInProgress"), 0);
        try {
            const data = formValues.reduce((obj: Record<string, string>, f: any) => {
                obj[f.name] = f.value;
                return obj;
            }, {});
            //metadata: '{"type":"application/macbinary","size":1048576,"category":"bin"}'
            const clientFiles = record.attachments.filter(
                (a: any) => a.direction === "in" && a.metadata?.includes("category\":\"bin\"")
            );
            const decrypted = clientFiles.filter((a: any) => a.name?.includes("decrypted"));
            const filesToAdd = decrypted.length > 0 ? decrypted : clientFiles;

            const zip = new JSZip();
            zip.file("winols.ini", generateIniFile({ ...data, account: record.account.name }));

            const usedNames = new Set<string>();
            const safeZipName = (name: string, index: number) => {
                const base = name || `file-${index}`;
                const sanitized = base.replace(/[/\\?*:|\s]/g, "_");
                if (!usedNames.has(sanitized)) {
                    usedNames.add(sanitized);
                    return sanitized;
                }
                const ext = sanitized.includes(".") ? sanitized.slice(sanitized.lastIndexOf(".")) : "";
                const stem = ext ? sanitized.slice(0, -ext.length) : sanitized;
                let candidate = `${stem}_${index}${ext}`;
                let n = index;
                while (usedNames.has(candidate)) candidate = `${stem}_${++n}${ext}`;
                usedNames.add(candidate);
                return candidate;
            };

            for (let i = 0; i < filesToAdd.length; i++) {
                const attachment = filesToAdd[i];
                console.log("Attachment:", attachment);
                const path = attachment?.file?.path;
                if (!path) continue;
                const res = await fetch(path);
                if (!res.ok) throw new Error(`Failed to fetch ${attachment.name || i}`);
                zip.file(safeZipName(attachment.name ?? `file-${i}`, i), await res.blob());
            }
            
            saveAs(await zip.generateAsync({ type: "blob" }), `order-${record.id}-files.zip`);
            hideLoading();
            message.success(_t("orders.actions.downloadZipSuccess"));
        } catch (err) {
            hideLoading();
            message.error(_t("orders.actions.downloadZipFailed"));
            console.error("Zip download error:", err);
        }
    };

    const updateProducts = (products: any[]) => {
        update(
            {
                resource: "orders",
                values: { productIds: products.map((v: number) => v.toString()) },
                id: record.id,
            },
            { onSuccess: () => invalidate({ resource: "orders", invalidates: ["detail"], id: record?.id }) }
        );
    };

    const handleProductChange = (values: any[]) => {
        updateProducts(values);
    };

    const isServiceCommentsDirty =
        (serviceComments ?? "") !== (record?.serviceComments ?? "");

    const onServiceCommentsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setServiceComments(e.target.value);
    };

    const handleSubmitServiceComments = () => {
        setIsSavingServiceComments(true);
        update(
            { resource: "orders", values: { serviceComments }, id: record.id },
            {
                onSuccess: () => {
                    setRecord((prev: any) => ({ ...prev, serviceComments }));
                },
                onSettled: () => {
                    setIsSavingServiceComments(false);
                },
            }
        );
    };

    const openSAV = () => {
        update(
            { resource: "orders", values: { status: OrderStatus.sav }, id: record.id },
            {
                onSuccess: () => {
                    setOrderStatus(OrderStatus.sav);
                    setRecord((prev: any) => ({ ...prev, status: OrderStatus.sav }));
                },
            }
        );
    };

    const closeSAV = () => {
        update(
            { resource: "orders", values: { status: OrderStatus.completed }, id: record.id },
            {
                onSuccess: () => {
                    setOrderStatus(OrderStatus.completed);
                    setRecord((prev: any) => ({ ...prev, status: OrderStatus.completed }));
                },
            }
        );
    };


    // ── File upload ────────────────────────────────────────────────────────────

    const fileUploadProps: UploadProps = {
        name: "file",
        action: apiUrl + "/files/upload",
        method: "POST",
        fileList,
        onChange(info) {
            setFileList([...info.fileList]);
            if (info.file.status === "done") {
                const res = info.file;
                if (res) {
                    const metadata = { type: res.type, size: res.size };
                    const attachment = {
                        name: res.name,
                        fileId: res.response.file.id,
                        metadata: JSON.stringify(metadata),
                        direction: "out",
                        orderId: `${record.id}`,
                    };
                    mutate(
                        { resource: "attachments", values: attachment },
                        {
                            onSuccess: () => {
                                setFileList([]);
                                update(
                                    {
                                        resource: "orders",
                                        values: { status: OrderStatus.completed },
                                        id: record.id,
                                    },
                                    {
                                        onSuccess: () => {
                                            markMessagesRead();
                                            setOrderStatus(OrderStatus.completed);
                                            setRecord((prev: any) => ({
                                                ...prev,
                                                status: OrderStatus.completed,
                                            }));
                                            invalidate({
                                                resource: "orders",
                                                invalidates: ["detail"],
                                                id: record?.id,
                                            });
                                        },
                                    }
                                );
                            },
                        }
                    );
                }
            }
        },
        progress: {
            strokeColor: {
                "0%": token.colorInfo,
                "100%": token.colorSuccess,
            },
            strokeWidth: 3,
            format: (percent) => percent && `${parseFloat(percent.toFixed(2))}%`,
        },
    };

    const incomingFileUploadProps: UploadProps = {
        name: "file",
        action: apiUrl + "/files/upload",
        method: "POST",
        fileList: incomingFileList,
        onChange(info) {
            setIncomingFileList([...info.fileList]);
            if (info.file.status === "done") {
                const res = info.file;
                if (res) {
                    const metadata = { type: res.type, size: res.size, category: incomingCategory };
                    const attachment = {
                        name: res.name,
                        fileId: res.response.file.id,
                        metadata: JSON.stringify(metadata),
                        direction: "in",
                        orderId: `${record.id}`,
                    };
                    mutate(
                        { resource: "attachments", values: attachment },
                        {
                            onSuccess: () => {
                                setIncomingFileList([]);
                                invalidate({
                                    resource: "orders",
                                    invalidates: ["detail"],
                                    id: record?.id,
                                });
                            },
                        }
                    );
                }
            }
        },
        progress: {
            strokeColor: {
                "0%": token.colorInfo,
                "100%": token.colorSuccess,
            },
            strokeWidth: 3,
            format: (percent) => percent && `${parseFloat(percent.toFixed(2))}%`,
        },
    };

    const messageFileUploadProps: UploadProps = {
        name: "file",
        action: apiUrl + "/files/upload",
        method: "POST",
        onChange(info) {
            setMessageFileList([...info.fileList]);
            if (info.file.status === "done") {
                const res = info.file;
                if (res) {
                    setMessageUploadedFiles((prev) => [
                        ...prev,
                        {
                            name: res.name,
                            fileId: res.response.file.id,
                            metadata: JSON.stringify({ type: res.type, size: res.size }),
                        },
                    ]);
                }
            }
        },
    };

    // ── Message sending ────────────────────────────────────────────────────────

    const sendMessage = (msg: string) => {
        const newMessage = {
            content: msg,
            orderId: `${record.id}`,
            senderId: `${(user as any)?.id}`,
            isRead: isAdmin,
        };

        mutate(
            { resource: "messages", values: newMessage },
            {
                onSuccess: (data) => {
                    const sentMessage = data.data;
                    orderMessages.appendLocal(sentMessage as OrderMessage);
                    let changingStatus = false;

                    if (!isAdmin && orderStatus === OrderStatus.completed) {
                        changingStatus = true;
                        update(
                            { resource: "orders", values: { status: OrderStatus.sav }, id: record.id },
                            {
                                onSuccess: () => {
                                    setOrderStatus(OrderStatus.sav);
                                    setRecord((prev: any) => ({ ...prev, status: OrderStatus.sav }));
                                    invalidate({
                                        resource: "orders",
                                        invalidates: ["detail"],
                                        id: record?.id,
                                    });
                                    void orderMessages.markAllRead();
                                },
                            }
                        );
                    }

                    const attachmentList = messageUploadedFiles.map((file) => ({
                        name: file.name,
                        fileId: file.fileId,
                        metadata: file.metadata,
                        messageId: `${sentMessage.id}`,
                        orderId: `${record.id}`,
                        direction: isAdmin ? "out" : "in",
                    }));

                    if (attachmentList.length > 0) {
                        mutateMany(
                            { resource: "attachments", values: attachmentList },
                            {
                                onSuccess: () => {
                                    setMessageUploadedFiles([]);
                                    invalidate({
                                        resource: "orders",
                                        invalidates: ["detail"],
                                        id: record?.id,
                                    });
                                    setMessageFileList([]);
                                    void orderMessages.refreshFirstPage();
                                },
                                onError: () => {
                                    open?.({
                                        message: _t("orders.errors.createAttachment"),
                                        type: "error",
                                        key: "unique-id",
                                    });
                                },
                            }
                        );
                    }
                },
            }
        );
    };

    const deleteResultAttachmentFn = (attachment: any) => {
        deleteResource(
            { resource: "attachments", id: attachment.id, values: attachment },
            {
                onSuccess: () => {
                    invalidate({ resource: "orders", invalidates: ["detail"], id: record?.id });
                    setResultAttachments((prev: any[]) => prev.filter((a) => a.id !== attachment.id));
                },
            }
        );
    };

    // ── Error state ────────────────────────────────────────────────────────────

    if (error) return <NotFound />;

    if (isLoading || !record) {
        if (isMobile) {
            return (
                <div className="px-3 pt-4 space-y-4">
                    <Skeleton active title paragraph={{ rows: 2 }} />
                    <Skeleton active paragraph={{ rows: 5 }} />
                    <Skeleton active paragraph={{ rows: 3 }} />
                </div>
            );
        }
        return (
            <Row gutter={[24, 24]}>
                <Col xs={24} xl={17}>
                    <GlassCard className="mb-4">
                        <Skeleton active title paragraph={{ rows: 2 }} />
                    </GlassCard>

                    <GlassCard className="mb-4">
                        <Skeleton active title paragraph={{ rows: 4 }} />
                    </GlassCard>

                    <Skeleton
                        active
                        title
                        paragraph={{ rows: 3 }}
                        className="mt-4"
                    />

                    <Row gutter={[{ xs: 8, sm: 16, md: 24 }, 24]} className="mt-4">
                        <Col xs={24} md={12}>
                            <GlassCard>
                                <Skeleton active paragraph={{ rows: 4 }} />
                            </GlassCard>
                        </Col>
                        <Col xs={24} md={12}>
                            <GlassCard>
                                <Skeleton active paragraph={{ rows: 4 }} />
                            </GlassCard>
                        </Col>
                    </Row>

                    <GlassCard className="mt-4">
                        <Skeleton active paragraph={{ rows: 5 }} />
                    </GlassCard>
                </Col>

                <Col xs={24} xl={7} className="pt-12">
                    <GlassCard className="mb-6">
                        <Skeleton active avatar paragraph={{ rows: 3 }} />
                    </GlassCard>

                    <GlassCard className="mb-6 !px-0">
                        <div className="px-4">
                            <Skeleton active title paragraph={{ rows: 4 }} />
                        </div>
                    </GlassCard>

                    <GlassCard>
                        <Skeleton active paragraph={{ rows: 4 }} />
                    </GlassCard>
                </Col>
            </Row>
        );
    }

    // ── Render ─────────────────────────────────────────────────────────────────

    const modals = (
        <>
            <AnalysisResultsModal
                open={automation.analysisModalVisible}
                onCancel={() => automation.setAnalysisModalVisible(false)}
                analysisResult={automation.analysisResult}
                tableColumns={automation.tableColumns}
                getTableData={automation.getTableData}
                attachments={attachments}
                isAssembling={automation.isAssembling}
                onRowClick={automation.handleRowClick}
                onFileDownload={automation.handleFileDownload}
                onAssemble={automation.handleAssemble}
            />
            <OrderFileDetailsModal
                open={automation.fileDetailsModalVisible}
                onCancel={() => automation.setFileDetailsModalVisible(false)}
                fileData={automation.selectedFileData}
                onDownload={automation.handleFileDownload}
            />
            <AssemblyFilesModal
                open={automation.assemblyFilesModalVisible}
                onCancel={() => automation.setAssemblyFilesModalVisible(false)}
                assemblyFiles={automation.assemblyFiles}
                assemblyFilesLoading={automation.assemblyFilesLoading}
                creatingAttachment={automation.creatingAttachment}
                onDownloadFile={automation.handleAssemblyFileDownload}
                onCreateAttachment={automation.handleCreateAttachmentFromModifiedBin}
            />
            <OrderSettingsModal
                open={settings.settingsModalVisible}
                onCancel={() => settings.setSettingsModalVisible(false)}
                autoStartOrder={settings.settingsAutoStartOrder}
                isSaving={settings.isSavingSettings}
                onAutoStartChange={settings.setSettingsAutoStartOrder}
                onSave={settings.handleSaveSettings}
            />
            <AssignmentModal
                open={assignment.assignmentModalVisible}
                onCancel={() => assignment.setAssignmentModalVisible(false)}
                assignedUserId={assignment.assignedUserId}
                assignedServiceProviderId={assignment.assignedServiceProviderId}
                assignedGroupId={assignment.assignedGroupId}
                assignmentStatus={record?.assignmentStatus}
                createdAt={record?.createdAt}
                usersData={assignment.usersData}
                serviceProvidersData={assignment.serviceProvidersData}
                groupsData={assignment.groupsData}
                update={update}
                invalidate={invalidate}
                record={record}
                isAdmin={isAdmin}
                _t={_t}
            />
            <ServiceProviderProductMappingModal
                open={assignment.productMappingModalOpen}
                serviceProviderId={assignment.assignedServiceProviderId ?? null}
                onCancel={() => assignment.setProductMappingModalOpen(false)}
                onSuccess={() => {
                    void assignment.handleProductMappingSuccess();
                }}
            />
        </>
    );

    if (isMobile) {
        return (
            <>
                <div className="px-3 pt-2 pb-1">
                    <OrderShowBar
                        record={record}
                        orderStatus={orderStatus}
                        canShowAssignToMe={assignment.canShowAssignToMe}
                        isAssigningToMe={assignment.isAssigningToMe}
                        onAssignToMe={assignment.handleAssignToMe}
                        onOpenSettingsModal={settings.handleOpenSettingsModal}
                        isAdmin={!!isAdmin}
                        isTuner={!!isTuner}
                        assignedUserId={assignment.assignedUserId}
                        assignedServiceProviderId={assignment.assignedServiceProviderId}
                        assignedGroupId={assignment.assignedGroupId}
                        assignmentStatus={record?.assignmentStatus}
                        usersData={assignment.usersData}
                        serviceProvidersData={assignment.serviceProvidersData}
                        groupsData={assignment.groupsData}
                        onOpenAssignmentModal={() => assignment.setAssignmentModalVisible(true)}
                        user={user}
                        onDownloadIni={downloadIniFile}
                        onDownloadIniAndClientFiles={downloadIniFileAndClientFiles}
                        onOpenSAV={openSAV}
                        onCloseSAV={closeSAV}
                        hasAvailableAutomation={!!automation.hasAvailableAutomation()}
                        onOpenAutomationModal={automation.handleOpenAutomationModal}
                        isMobile={true}
                    />
                </div>

                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    tabBarStyle={{
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                        margin: 0,
                        paddingLeft: 12,
                        background: token.colorBgElevated,
                    }}
                    items={[
                        {
                            key: "details",
                            label: _t("orders.tabs.details"),
                            children: (
                                <div className="px-3 pb-6">
                                    <div className="mb-3 pb-3" style={{ borderBottom: `1px solid ${token.colorBorderSecondary}` }}>
                                        {record && <OrderShowHeader order={record} field={field} isMobile={true} />}
                                    </div>
                                    <OrderCharacteristics formValues={formValues} />
                                    <Row gutter={[0, 12]} className="mt-3">
                                        <Col span={24}>
                                            <OrderProducts
                                                selectedProducts={selectedProducts}
                                                productOptions={productOptions}
                                                isAdmin={isAdmin}
                                                onProductChange={handleProductChange}
                                            />
                                        </Col>
                                        <Col span={24}>
                                            <OrderComments comments={record?.comments} />
                                        </Col>
                                    </Row>
                                </div>
                            ),
                        },
                        {
                            key: "messages",
                            label: _t("orders.tabs.messages"),
                            children: (
                                <Messages
                                    messages={orderMessages.messages}
                                    sendMessage={sendMessage}
                                    fileUploadProps={messageFileUploadProps}
                                    fileList={messageFileList}
                                    maxHeight={window.innerHeight - 260}
                                    hasMore={orderMessages.hasMore}
                                    isLoadingInitial={orderMessages.isLoadingInitial}
                                    isLoadingMore={orderMessages.isLoadingMore}
                                    loadOlder={orderMessages.loadOlder}
                                />
                            ),
                        },
                        {
                            key: "attachments",
                            label: _t("orders.tabs.attachments"),
                            children: (
                                <div className="px-3 pb-6">
                                    <OrderAttachments
                                        attachments={attachments}
                                        resultAttachments={resultAttachments}
                                        isAdmin={isAdmin}
                                        serviceComments={serviceComments}
                                        onServiceCommentsChange={onServiceCommentsChange}
                                        onServiceCommentsSubmit={handleSubmitServiceComments}
                                        canSubmitServiceComments={isServiceCommentsDirty}
                                        isSavingServiceComments={isSavingServiceComments}
                                        onDeleteAttachment={deleteResultAttachmentFn}
                                        fileUploadProps={fileUploadProps}
                                        incomingFileUploadProps={incomingFileUploadProps}
                                        incomingCategory={incomingCategory}
                                        onIncomingCategoryChange={setIncomingCategory}
                                    />
                                </div>
                            ),
                        },
                        {
                            key: "history",
                            label: _t("orders.tabs.history"),
                            children: (
                                <div className="px-3 pb-6">
                                    {isAdmin && (
                                        <div className="mb-4">
                                            <OrderAutomationTasks
                                                automationTasks={record?.automationTasks}
                                                onOpenFilesModal={(analysisResult: any) => {
                                                    automation.setAnalysisResult(analysisResult);
                                                    automation.setAnalysisModalVisible(true);
                                                }}
                                                onOpenAssemblyFilesModal={automation.handleOpenAssemblyFilesModal}
                                                onRerunTask={automation.handleRerunTask}
                                                rerunningTaskId={automation.rerunningTaskId}
                                            />
                                        </div>
                                    )}
                                    <OrderHistoryTimeline order={record} />
                                </div>
                            ),
                        },
                    ]}
                />
                {modals}
            </>
        );
    }

    return (
        <>
            <Row gutter={[24, 24]}>
                <Col xs={24} xl={17}>
                    <OrderShowBar
                        record={record}
                        orderStatus={orderStatus}
                        canShowAssignToMe={assignment.canShowAssignToMe}
                        isAssigningToMe={assignment.isAssigningToMe}
                        onAssignToMe={assignment.handleAssignToMe}
                        onOpenSettingsModal={settings.handleOpenSettingsModal}
                        isAdmin={!!isAdmin}
                        isTuner={!!isTuner}
                        assignedUserId={assignment.assignedUserId}
                        user={user}
                        onDownloadIni={downloadIniFile}
                        onDownloadIniAndClientFiles={downloadIniFileAndClientFiles}
                        onOpenSAV={openSAV}
                        onCloseSAV={closeSAV}
                        hasAvailableAutomation={!!automation.hasAvailableAutomation()}
                        onOpenAutomationModal={automation.handleOpenAutomationModal}
                    />

                    <GlassCard className="mb-4">
                        {record && <OrderShowHeader order={record} field={field} />}
                    </GlassCard>

                    <OrderCharacteristics formValues={formValues} />

                    <Typography.Title
                        level={4}
                        style={{ color: token.colorPrimaryText }}
                        className="mt-8"
                    >
                        <CodeOutlined className="mr-2" style={{ color: token.colorPrimary }} />
                        {_t("orders.fields.reprogramming")}
                    </Typography.Title>

                    <Row gutter={[{ xs: 8, sm: 16, md: 24 }, 24]} className="mt-2">
                        <Col xs={24} md={12}>
                            <OrderProducts
                                selectedProducts={selectedProducts}
                                productOptions={productOptions}
                                isAdmin={isAdmin}
                                onProductChange={handleProductChange}
                            />
                        </Col>
                        <Col xs={24} md={12}>
                            <OrderComments comments={record?.comments} />
                        </Col>
                    </Row>

                    <OrderAttachments
                        attachments={attachments}
                        resultAttachments={resultAttachments}
                        isAdmin={isAdmin}
                        serviceComments={serviceComments}
                        onServiceCommentsChange={onServiceCommentsChange}
                        onServiceCommentsSubmit={handleSubmitServiceComments}
                        canSubmitServiceComments={isServiceCommentsDirty}
                        isSavingServiceComments={isSavingServiceComments}
                        onDeleteAttachment={deleteResultAttachmentFn}
                        fileUploadProps={fileUploadProps}
                        incomingFileUploadProps={incomingFileUploadProps}
                        incomingCategory={incomingCategory}
                        onIncomingCategoryChange={setIncomingCategory}
                    />
                </Col>

                <Col xs={24} xl={7} className="pt-12">
                    {isAdmin && (
                        <div className="mb-6">
                            <OrderAssignmentCard
                                assignedUserId={assignment.assignedUserId}
                                assignedServiceProviderId={assignment.assignedServiceProviderId}
                                assignedGroupId={assignment.assignedGroupId}
                                assignmentStatus={record?.assignmentStatus}
                                createdAt={record?.createdAt}
                                usersData={assignment.usersData}
                                serviceProvidersData={assignment.serviceProvidersData}
                                groupsData={assignment.groupsData}
                                orderProducts={record?.products}
                                externalPushStatus={assignment.externalPushStatus}
                                onOpenModal={() => assignment.setAssignmentModalVisible(true)}
                                onOpenProductMapping={() =>
                                    assignment.setProductMappingModalOpen(true)
                                }
                                onRetryExternalPush={assignment.handleRetryExternalPush}
                                isRetryingExternalPush={assignment.isRetryingExternalPush}
                                isAdmin={isAdmin}
                                _t={_t}
                            />

                        </div>
                    )}

                    {isAdmin && (
                        <div className="mb-6">
                            <OrderAutomationTasks
                                automationTasks={record?.automationTasks}
                                onOpenFilesModal={(analysisResult: any) => {
                                    automation.setAnalysisResult(analysisResult);
                                    automation.setAnalysisModalVisible(true);
                                }}
                                onOpenAssemblyFilesModal={automation.handleOpenAssemblyFilesModal}
                                onRerunTask={automation.handleRerunTask}
                                rerunningTaskId={automation.rerunningTaskId}
                            />
                        </div>
                    )}

                    <GlassCard className="hidden xl:block !px-0">
                        <Flex className="px-4 mb-6" gap="small">
                            <MessageOutlined style={{ color: token.colorPrimary }} />
                            <Typography.Title level={5} style={{ margin: 0, fontWeight: "bold" }}>
                                {_t("orders.titles.aftersales")}
                            </Typography.Title>
                        </Flex>
                        <Messages
                            messages={orderMessages.messages}
                            sendMessage={sendMessage}
                            fileUploadProps={messageFileUploadProps}
                            fileList={messageFileList}
                            maxHeight={500}
                            hasMore={orderMessages.hasMore}
                            isLoadingInitial={orderMessages.isLoadingInitial}
                            isLoadingMore={orderMessages.isLoadingMore}
                            loadOlder={orderMessages.loadOlder}
                        />
                    </GlassCard>

                    <div className="mt-6">
                        <OrderHistoryTimeline order={record} />
                    </div>
                </Col>
            </Row>

            {/* Modals */}
            {modals}
        </>
    );
};
